import { Box, Button, ButtonProps, CircularProgress } from '@mui/material'
import { Colors } from '../../theme/Colors'
import { OnFoxClick } from './FoxButtonBase'
import { Link } from 'react-router-dom'
import React from 'react'

export type FoxButtonProps = {
    text: string
    loading?: boolean
    disabled?: boolean
    variant?: ButtonVariant
    primary?: boolean
    grey?: boolean
    onFoxClick: OnFoxClick
    startIcon?: any
    endIcon?: any
    showBetaChip?: boolean // TODO: Consider just making this an endIcon
} & ButtonProps

type ButtonVariant = 'text' | 'outlined' | 'contained'

const ButtonSizeToProgressSizeMap = {
    small: 16,
    medium: 20,
    large: 26
}

export const FoxButton = (props: FoxButtonProps) => {
    const { text, loading, disabled, startIcon, endIcon, showBetaChip, size, primary, grey, variant, onFoxClick, sx, onClick: _, ...rest } = props
    const disabledCSS = disabled ? styles.disabled : {}

    const color = primary ? 'primary' : 'secondary'

    const progressSize = ButtonSizeToProgressSizeMap[size || 'medium']

    const background = primary ? 'linear-gradient(84.45deg, #E84855 0%, #E87155 100%)' : grey ? Colors.light.buttonGrey : 'white'
    const textColor = primary ? 'white' : Colors.light.tertiary

    const component = onFoxClick.kind === 'external' ? 'a' : onFoxClick.kind === 'internal' ? Link : 'button'
    const href = onFoxClick.kind === 'external' ? onFoxClick.href : undefined
    const to = onFoxClick.kind === 'internal' ? onFoxClick.to : undefined

    const [awaitingOnClick, setAwaitingOnClick] = React.useState(false)
    const isButtonLoading = loading || awaitingOnClick

    const disabledTextColorCSS = isButtonLoading ? { color: 'transparent' } : {}

    let onClick: ((e: React.MouseEvent<HTMLAnchorElement>) => Promise<void> | void) | undefined
    if (onFoxClick.kind === 'button') {
        if (onFoxClick.preventDoubleClick) {
            onClick = async (e) => {
                if (awaitingOnClick) {
                    return
                }

                setAwaitingOnClick(true)
                try {
                    await onFoxClick.onClick(e)
                } finally {
                    setAwaitingOnClick(false)
                }
            }
        } else {
            onClick = onFoxClick.onClick
        }
    }

    return (
        <Button
            disableRipple
            disabled={disabled || isButtonLoading}
            variant={variant}
            color={color}
            size={size}
            href={href}
            component={component}
            to={to}
            onClick={onClick}
            startIcon={startIcon}
            endIcon={endIcon}
            target={onFoxClick.kind === 'external' ? '_blank' : undefined}
            referrer={onFoxClick.kind === 'external' ? 'noreferrer' : undefined}
            disableElevation
            sx={{
                ...styles.button,
                ...disabledCSS,
                background,
                color: textColor,
                '&.Mui-disabled': disabledTextColorCSS,
                justifyContent: isButtonLoading ? 'center' : 'flex-start',
                ...sx
            }}
            {...rest}>
            {text}
            {isButtonLoading && (
                <Box sx={{ display: 'flex', alignContent: 'center', justifyContent: 'center', height: '24px', position: 'absolute' }}>
                    <CircularProgress size={progressSize} sx={{ color: primary ? 'white' : Colors.light.tertiary }} />
                </Box>
            )}
        </Button>
    )
}

const styles = {
    button: {
        justifyContent: 'flex-start',
        width: 'fit-content',
        borderRadius: '10px',
        height: '48px'
    },
    disabled: {
        opacity: 0.7
    }
}
